/**
 * InventoryPage — internal staff inventory surface (#153, Workstream 2 STEP 3).
 *
 * Central (staff) tenancy only. Three tabs over routes/inventory.js:
 *   - Catalog: items with aggregated on-hand; expand an item to see its lots,
 *     receive stock, adjust, and view the ledger. New item + new lot forms.
 *   - Alerts: low-stock items (on-hand <= reorder point) + near-expiry lots.
 *   - Fulfillment: the affiliate partner supply-order queue — approve, fulfill
 *     (decrements a central lot + creates the partner lot), or cancel.
 *
 * Self-gates on inventory:read; write actions gate on inventory:write and
 * fulfillment actions on inventory:fulfill. The server enforces the same
 * independently. No PHI on this surface — product/lot/quantity data only.
 *
 * The affiliate PARTNER supply portal is a SEPARATE surface (PR-B) reached
 * through securePartnerHandler — nothing here is partner-reachable.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Package, Loader2, RefreshCcw, AlertCircle, AlertTriangle,
  Plus, ChevronDown, ChevronRight, PackageCheck, Clock,
} from "lucide-react";

const CATEGORIES = [
  ["peptide", "Peptide"], ["stem_cell", "Stem cell"], ["medication", "Medication"],
  ["supply", "Supply"], ["other", "Other"],
];
const MODALITIES = [
  ["", "—"], ["msc", "MSC"], ["muse", "Muse"], ["exosome", "Exosome"],
  ["prp", "PRP"], ["nk", "NK"], ["peptide", "Peptide"], ["other", "Other"],
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES);

function hasPerm(user, p) { return new Set((user && user.permissions) || []).has(p); }
function fmtDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
function daysUntil(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return Math.ceil((dt.getTime() - Date.now()) / 86400000);
}
function num(v) { return v == null ? 0 : Number(v); }

export default function InventoryPage({ user }) {
  const canRead = hasPerm(user, "inventory:read");
  const canWrite = hasPerm(user, "inventory:write");
  const canFulfill = hasPerm(user, "inventory:fulfill");

  const [tab, setTab] = useState("catalog");
  const [error, setError] = useState(null);

  if (!canRead) {
    return (
      <div style={wrap}>
        <div style={accessDenied}>
          <AlertCircle size={16} /> You do not have permission to view inventory.
        </div>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <div style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Package size={22} color="#2E8B98" />
          <div>
            <h1 style={{ fontSize: 21, margin: 0 }}>Inventory</h1>
            <p style={{ color: "#5a6478", fontSize: 13, margin: 0 }}>
              Central catalog, lots, and stock ledger — plus the partner supply-order queue.
            </p>
          </div>
        </div>
      </div>

      <div style={tabBar}>
        <TabButton active={tab === "catalog"} onClick={() => setTab("catalog")}>Catalog</TabButton>
        <TabButton active={tab === "alerts"} onClick={() => setTab("alerts")}>Alerts</TabButton>
        <TabButton active={tab === "fulfillment"} onClick={() => setTab("fulfillment")}>Fulfillment</TabButton>
      </div>

      {error && (
        <div style={errBox}><AlertCircle size={14} /> {error}</div>
      )}

      {tab === "catalog" && <CatalogTab canWrite={canWrite} setError={setError} />}
      {tab === "alerts" && <AlertsTab setError={setError} />}
      {tab === "fulfillment" && <FulfillmentTab canFulfill={canFulfill} setError={setError} />}
    </div>
  );
}

// ─── Catalog ────────────────────────────────────────────────────────────────
function CatalogTab({ canWrite, setError }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showItemForm, setShowItemForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.invoke("inventory:items:list", {});
      setItems(Array.isArray(r) ? r : []);
    } catch (e) { setError((e && e.message) || "Failed to load catalog"); }
    setLoading(false);
  }, [setError]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 24, color: "#5a6478" }}>Loading catalog…</div>;

  return (
    <div>
      <div style={toolbar}>
        <button onClick={load} style={btnSecondary}><RefreshCcw size={13} /> Refresh</button>
        {canWrite && (
          <button onClick={() => setShowItemForm((v) => !v)} style={btnPrimary}>
            <Plus size={13} /> New item
          </button>
        )}
      </div>

      {showItemForm && canWrite && (
        <ItemForm onSaved={() => { setShowItemForm(false); load(); }} onCancel={() => setShowItemForm(false)} setError={setError} />
      )}

      {items.length === 0 ? (
        <div style={emptyState}>No inventory items yet.</div>
      ) : (
        <div style={card}>
          <div className="responsive-scroll">
          <table style={{ ...table, minWidth: 640 }}>
            <thead>
              <tr>
                <th style={th}></th>
                <th style={th}>Item</th>
                <th style={th}>Category</th>
                <th style={thNum}>On hand</th>
                <th style={thNum}>Reorder pt</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <React.Fragment key={it.id}>
                  <tr style={{ cursor: "pointer" }} onClick={() => setExpanded(expanded === it.id ? null : it.id)}>
                    <td style={td}>{expanded === it.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{it.name}{it.is_biologic ? <span style={bioTag}>biologic</span> : null}</td>
                    <td style={td}>{CATEGORY_LABEL[it.category] || it.category}</td>
                    <td style={tdNum}>{num(it.total_on_hand).toLocaleString()} {it.unit}</td>
                    <td style={tdNum}>{num(it.reorder_point).toLocaleString()}</td>
                    <td style={td}>
                      {it.below_reorder
                        ? <span style={pillWarn}><AlertTriangle size={11} /> Low</span>
                        : <span style={pillOk}>OK</span>}
                    </td>
                  </tr>
                  {expanded === it.id && (
                    <tr>
                      <td style={{ padding: 0 }} colSpan={6}>
                        <ItemLots item={it} canWrite={canWrite} setError={setError} onChange={load} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemForm({ onSaved, onCancel, setError }) {
  const [f, setF] = useState({ name: "", category: "supply", unit: "", isBiologic: false, reorderPoint: "0", sku: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  async function save() {
    setBusy(true); setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("inventory:items:upsert", {
        name: f.name, category: f.category, unit: f.unit, isBiologic: f.isBiologic,
        reorderPoint: f.reorderPoint === "" ? 0 : Number(f.reorderPoint), sku: f.sku || null,
      });
      if (r && r.error) setError(r.message || r.code);
      else onSaved();
    } catch (e) { setError((e && e.message) || "Save failed"); }
    setBusy(false);
  }
  return (
    <div style={formCard}>
      <div style={formGrid}>
        <div><label style={lbl}>Name</label><input value={f.name} onChange={set("name")} style={inp} placeholder="e.g. BPC-157 5mg vial" /></div>
        <div><label style={lbl}>Category</label>
          <select value={f.category} onChange={set("category")} style={inp}>
            {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Unit</label><input value={f.unit} onChange={set("unit")} style={inp} placeholder="vial / ml / mg / unit" /></div>
        <div><label style={lbl}>Reorder point</label><input type="number" min="0" value={f.reorderPoint} onChange={set("reorderPoint")} style={inp} /></div>
        <div><label style={lbl}>SKU (optional)</label><input value={f.sku} onChange={set("sku")} style={inp} placeholder="links to price list" /></div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
          <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6, margin: 0 }}>
            <input type="checkbox" checked={f.isBiologic} onChange={set("isBiologic")} /> Biologic (cold chain / QC)
          </label>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button onClick={save} disabled={busy || !f.name.trim() || !f.unit.trim()} style={btnPrimary}>
          {busy ? <><Loader2 size={12} className="spin" /> Saving…</> : "Save item"}
        </button>
        <button onClick={onCancel} style={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}

function ItemLots({ item, canWrite, setError, onChange }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLotForm, setShowLotForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.invoke("inventory:lots:list", { itemId: item.id });
      setLots(Array.isArray(r) ? r : []);
    } catch (e) { setError((e && e.message) || "Failed to load lots"); }
    setLoading(false);
  }, [item.id, setError]);
  useEffect(() => { load(); }, [load]);

  async function adjust(lot) {
    const raw = window.prompt(`Signed adjustment for lot ${lot.lot_number} (e.g. -2 or 5):`);
    if (raw == null) return;
    const delta = Number(raw);
    if (!Number.isFinite(delta) || delta === 0) { setError("Enter a non-zero number"); return; }
    const note = window.prompt("Reason for adjustment (required, logged):");
    if (!note || !note.trim()) return;
    try {
      const r = await window.zignagenixAPI.invoke("inventory:adjust", { lotId: lot.id, delta, note: note.trim() });
      if (r && r.error) setError(r.message || r.code);
      else { await load(); onChange && onChange(); }
    } catch (e) { setError((e && e.message) || "Adjust failed"); }
  }

  async function receive(lot) {
    const raw = window.prompt(`Quantity to receive into lot ${lot.lot_number}:`);
    if (raw == null) return;
    const qty = Number(raw);
    if (!Number.isFinite(qty) || qty <= 0) { setError("Enter a positive number"); return; }
    try {
      const r = await window.zignagenixAPI.invoke("inventory:receive", { lotId: lot.id, qty });
      if (r && r.error) setError(r.message || r.code);
      else { await load(); onChange && onChange(); }
    } catch (e) { setError((e && e.message) || "Receive failed"); }
  }

  return (
    <div style={lotsWrap}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.04em" }}>Lots</span>
        {canWrite && <button onClick={() => setShowLotForm((v) => !v)} style={btnGhost}><Plus size={12} /> New lot</button>}
      </div>
      {showLotForm && canWrite && (
        <LotForm itemId={item.id} onSaved={() => { setShowLotForm(false); load(); onChange && onChange(); }} onCancel={() => setShowLotForm(false)} setError={setError} />
      )}
      {loading ? (
        <div style={{ fontSize: 12, color: "#9aa4b2" }}>Loading lots…</div>
      ) : lots.length === 0 ? (
        <div style={{ fontSize: 12, color: "#9aa4b2", fontStyle: "italic" }}>No lots on file.</div>
      ) : (
        <div className="responsive-scroll">
        <table style={{ ...table, minWidth: 680 }}>
          <thead>
            <tr>
              <th style={th}>Lot</th><th style={th}>Modality</th><th style={thNum}>On hand</th>
              <th style={th}>Expiration</th><th style={th}>Location</th><th style={th}>QC</th>
              {canWrite && <th style={th}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {lots.map((l) => {
              const d = daysUntil(l.expiration);
              const nearExp = d != null && d <= 30 && num(l.qty_on_hand) > 0;
              return (
                <tr key={l.id}>
                  <td style={{ ...td, fontFamily: "monospace" }}>{l.lot_number}</td>
                  <td style={td}>{l.modality || "—"}</td>
                  <td style={tdNum}>{num(l.qty_on_hand).toLocaleString()}</td>
                  <td style={td}>
                    {fmtDate(l.expiration) || "—"}
                    {nearExp && <span style={pillWarn}><Clock size={10} /> {d < 0 ? "expired" : `${d}d`}</span>}
                  </td>
                  <td style={td}>{l.cold_chain_location || "—"}</td>
                  <td style={td}>{l.qc_certificate_id ? <span style={pillOk}>on file</span> : <span style={{ color: "#9aa4b2", fontSize: 11 }}>—</span>}</td>
                  {canWrite && (
                    <td style={td}>
                      <button onClick={() => receive(l)} style={btnGhost}>Receive</button>{" "}
                      <button onClick={() => adjust(l)} style={btnGhost}>Adjust</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

function LotForm({ itemId, onSaved, onCancel, setError }) {
  const [f, setF] = useState({ lotNumber: "", modality: "", expiration: "", coldChainLocation: "", qtyReceived: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  async function save() {
    setBusy(true); setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("inventory:lots:create", {
        itemId, lotNumber: f.lotNumber, modality: f.modality || null,
        expiration: f.expiration || null, coldChainLocation: f.coldChainLocation || null,
        qtyReceived: f.qtyReceived === "" ? 0 : Number(f.qtyReceived),
      });
      if (r && r.error) setError(r.message || r.code);
      else onSaved();
    } catch (e) { setError((e && e.message) || "Save failed"); }
    setBusy(false);
  }
  return (
    <div style={formCard}>
      <div style={formGrid}>
        <div><label style={lbl}>Lot number</label><input value={f.lotNumber} onChange={set("lotNumber")} style={inp} placeholder="e.g. MSC-2406-A" /></div>
        <div><label style={lbl}>Modality</label>
          <select value={f.modality} onChange={set("modality")} style={inp}>
            {MODALITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div><label style={lbl}>Expiration</label><input type="date" value={f.expiration} onChange={set("expiration")} style={inp} /></div>
        <div><label style={lbl}>Cold-chain location</label><input value={f.coldChainLocation} onChange={set("coldChainLocation")} style={inp} placeholder="freezer / fridge / bin" /></div>
        <div><label style={lbl}>Qty received</label><input type="number" min="0" value={f.qtyReceived} onChange={set("qtyReceived")} style={inp} /></div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button onClick={save} disabled={busy || !f.lotNumber.trim()} style={btnPrimary}>
          {busy ? <><Loader2 size={12} className="spin" /> Saving…</> : "Save lot"}
        </button>
        <button onClick={onCancel} style={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Alerts ─────────────────────────────────────────────────────────────────
function AlertsTab({ setError }) {
  const [data, setData] = useState({ lowStock: [], nearExpiry: [] });
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.invoke("inventory:alerts", {});
      setData(r && !r.error ? r : { lowStock: [], nearExpiry: [] });
    } catch (e) { setError((e && e.message) || "Failed to load alerts"); }
    setLoading(false);
  }, [setError]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 24, color: "#5a6478" }}>Loading alerts…</div>;

  return (
    <div>
      <div style={toolbar}><button onClick={load} style={btnSecondary}><RefreshCcw size={13} /> Refresh</button></div>
      <div style={card}>
        <div style={sectionHeader}><AlertTriangle size={14} color="#b45309" /> Low stock ({data.lowStock.length})</div>
        {data.lowStock.length === 0 ? <div style={emptyInline}>Nothing at or below its reorder point.</div> : (
          <div className="responsive-scroll">
          <table style={{ ...table, minWidth: 520 }}>
            <thead><tr><th style={th}>Item</th><th style={th}>Category</th><th style={thNum}>On hand</th><th style={thNum}>Reorder pt</th></tr></thead>
            <tbody>
              {data.lowStock.map((r) => (
                <tr key={r.id}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
                  <td style={td}>{CATEGORY_LABEL[r.category] || r.category}</td>
                  <td style={tdNum}>{num(r.total_on_hand).toLocaleString()} {r.unit}</td>
                  <td style={tdNum}>{num(r.reorder_point).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <div style={{ ...card, marginTop: 14 }}>
        <div style={sectionHeader}><Clock size={14} color="#b45309" /> Near expiry ({data.nearExpiry.length}) — next {data.nearExpiryDays || 30} days</div>
        {data.nearExpiry.length === 0 ? <div style={emptyInline}>No lots expiring soon.</div> : (
          <div className="responsive-scroll">
          <table style={{ ...table, minWidth: 620 }}>
            <thead><tr><th style={th}>Item</th><th style={th}>Lot</th><th style={th}>Modality</th><th style={thNum}>On hand</th><th style={th}>Expiration</th></tr></thead>
            <tbody>
              {data.nearExpiry.map((r) => {
                const d = daysUntil(r.expiration);
                return (
                  <tr key={r.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.item_name}</td>
                    <td style={{ ...td, fontFamily: "monospace" }}>{r.lot_number}</td>
                    <td style={td}>{r.modality || "—"}</td>
                    <td style={tdNum}>{num(r.qty_on_hand).toLocaleString()}</td>
                    <td style={td}>{fmtDate(r.expiration)} <span style={pillWarn}>{d < 0 ? "expired" : `${d}d`}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Fulfillment ──────────────────────────────────────────────────────────────
function FulfillmentTab({ canFulfill, setError }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.invoke("inventory:fulfillment:queue", {});
      setOrders(Array.isArray(r) ? r : []);
    } catch (e) { setError((e && e.message) || "Failed to load queue"); }
    setLoading(false);
  }, [setError]);
  useEffect(() => { load(); }, [load]);

  async function act(order, channel, label) {
    setBusy(order.id); setError(null);
    try {
      const r = await window.zignagenixAPI.invoke(channel, { orderId: order.id });
      if (r && r.error) setError(`${label} failed: ${r.message || r.code}`);
      else await load();
    } catch (e) { setError((e && e.message) || `${label} failed`); }
    setBusy(null);
  }

  if (loading) return <div style={{ padding: 24, color: "#5a6478" }}>Loading fulfillment queue…</div>;

  return (
    <div>
      <div style={toolbar}><button onClick={load} style={btnSecondary}><RefreshCcw size={13} /> Refresh</button></div>
      {orders.length === 0 ? (
        <div style={emptyState}>No open partner supply orders.</div>
      ) : (
        <div style={card}>
          <div className="responsive-scroll">
          <table style={{ ...table, minWidth: 780 }}>
            <thead>
              <tr>
                <th style={th}>Partner</th><th style={th}>Item</th><th style={thNum}>Requested</th>
                <th style={thNum}>Central stock</th><th style={th}>Status</th><th style={th}>Requested at</th>
                {canFulfill && <th style={th}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const short = num(o.central_on_hand) < num(o.qty_requested);
                return (
                  <tr key={o.id}>
                    <td style={{ ...td, fontWeight: 600 }}>{o.organization_name}</td>
                    <td style={td}>{o.item_name}</td>
                    <td style={tdNum}>{num(o.qty_requested).toLocaleString()} {o.unit}</td>
                    <td style={{ ...tdNum, color: short ? "#b00020" : "#2d6b2d" }}>{num(o.central_on_hand).toLocaleString()}</td>
                    <td style={td}><span style={o.status === "approved" ? pillOk : pillNeutral}>{o.status}</span></td>
                    <td style={td}>{fmtDate(o.requested_at) || "—"}</td>
                    {canFulfill && (
                      <td style={td}>
                        {o.status === "requested" && (
                          <button onClick={() => act(o, "inventory:order:approve", "Approve")} disabled={busy === o.id} style={btnGhost}>Approve</button>
                        )}{" "}
                        <button
                          onClick={() => act(o, "inventory:order:fulfill", "Fulfill")}
                          disabled={busy === o.id || short}
                          title={short ? "Insufficient central stock" : "Fulfill from central stock"}
                          style={{ ...btnGhost, opacity: short ? 0.5 : 1 }}
                        >
                          <PackageCheck size={12} /> Fulfill
                        </button>{" "}
                        <button onClick={() => act(o, "inventory:order:cancel", "Cancel")} disabled={busy === o.id} style={btnGhostDanger}>Cancel</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Small presentational bits ────────────────────────────────────────────────
function TabButton({ active, onClick, children }) {
  return <button onClick={onClick} style={active ? tabActive : tabInactive}>{children}</button>;
}

// ─── Styles (module scope — stable references, #129 render discipline) ─────────
const wrap = { padding: "16px 24px", maxWidth: 1400, margin: "0 auto" };
const header = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 };
const tabBar = { display: "flex", gap: 6, borderBottom: "1px solid #e1e6ed", marginBottom: 16 };
const tabInactive = { padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#5a6478", background: "none", border: "none", borderBottom: "2px solid transparent", cursor: "pointer" };
const tabActive = { ...tabInactive, color: "#2E8B98", borderBottom: "2px solid #2E8B98" };
const toolbar = { display: "flex", gap: 8, marginBottom: 12, justifyContent: "flex-end" };
const card = { background: "#fff", border: "1px solid #e1e6ed", borderRadius: 6, overflow: "hidden" };
const table = { width: "100%", borderCollapse: "collapse", fontSize: 12.5 };
const th = { textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: "1px solid #e1e6ed", background: "#f7f9fc" };
const thNum = { ...th, textAlign: "right" };
const td = { padding: "8px 12px", borderBottom: "1px solid #f0f3f7", color: "#1F2937", verticalAlign: "middle" };
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const lotsWrap = { background: "#FAFBFC", padding: "12px 16px", borderBottom: "2px solid #e1e6ed" };
const sectionHeader = { display: "flex", alignItems: "center", gap: 6, padding: "10px 12px", fontSize: 12.5, fontWeight: 700, color: "#3a4a5e", background: "#f7f9fc", borderBottom: "1px solid #e1e6ed" };
const formCard = { background: "#FAFBFC", border: "1px solid #e1e6ed", borderRadius: 6, padding: 12, marginBottom: 12 };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 };
const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#3a4a5e", margin: "0 0 2px" };
const inp = { width: "100%", padding: "6px 8px", fontSize: 12, color: "#1f2a3b", border: "1px solid #c9d6e8", borderRadius: 4, background: "#fff", boxSizing: "border-box", fontFamily: "inherit" };
const btnPrimary = { padding: "6px 12px", fontSize: 12, fontWeight: 600, background: "#2E8B98", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const btnSecondary = { padding: "6px 12px", fontSize: 12, fontWeight: 600, background: "#fff", color: "#5a6478", border: "1px solid #DEE2E6", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 };
const btnGhost = { padding: "3px 9px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#2E8B98", border: "1px solid #cfe3e6", borderRadius: 5, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 };
const btnGhostDanger = { ...btnGhost, color: "#b00020", border: "1px solid #f0c0c0" };
const bioTag = { marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#6b1f8b", background: "#f6eefa", padding: "1px 6px", borderRadius: 8, textTransform: "uppercase", letterSpacing: "0.03em" };
const pillOk = { fontSize: 11, fontWeight: 700, color: "#2d6b2d", background: "#eef6ee", padding: "2px 8px", borderRadius: 10 };
const pillNeutral = { fontSize: 11, fontWeight: 700, color: "#5a6478", background: "#eef1f5", padding: "2px 8px", borderRadius: 10 };
const pillWarn = { display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 6, fontSize: 11, fontWeight: 700, color: "#b45309", background: "#fdf6e9", padding: "2px 8px", borderRadius: 10 };
const emptyState = { padding: 40, textAlign: "center", color: "#9aa4b2", fontStyle: "italic", background: "#fff", border: "1px dashed #d5dce6", borderRadius: 6 };
const emptyInline = { padding: "12px", fontSize: 12, color: "#9aa4b2", fontStyle: "italic" };
const errBox = { display: "flex", alignItems: "center", gap: 6, margin: "0 0 12px", padding: "8px 12px", borderRadius: 4, fontSize: 12.5, background: "#fde8e8", border: "1px solid #f5a5a5", color: "#8b0000" };
const accessDenied = { display: "flex", alignItems: "center", gap: 8, padding: 20, fontSize: 13, color: "#8b0000", background: "#fde8e8", border: "1px solid #f5a5a5", borderRadius: 6 };
